import { useEffect, useRef, useState } from 'react';

import { useAuth } from './AuthenticationContext.jsx';
import BackLink from '../navigation/BackLink.jsx';
import HaloLoginDevices from '../components/HaloLoginDevices.jsx';
import LegalFooter from '../legal/LegalFooter.jsx';
import TextField from '../components/TextField.jsx';

// Neutral, always-identical confirmation. It never reveals whether the email has
// an account (avoids account enumeration), and it points the user at the reset
// email — whose link opens the existing /password-code bridge — or the app.
const CONFIRMATION =
  "If that email address has a Halo account, we've sent a password-reset link. Open the Halo app, or the link in the email, to finish resetting your password.";

// A failed request is only kept silent (shown as the neutral confirmation) when it
// is a RESPONSE from Cognito — those must look identical whether or not the email
// has an account, or we'd leak which emails are registered. A TRANSPORT failure is
// different: the request never reached Cognito, so it reveals nothing about the
// account, and hiding it would falsely tell the user a link was sent when nothing
// left their device. Surface those so they can retry. (Cognito exceptions carry
// specific names like UserNotFoundException; only genuine transport failures match
// here, so an account-existence signal can never slip through.)
function isTransportError(err) {
  return (
    err?.name === 'NetworkError' ||
    err?.name === 'TypeError' || // a raw fetch failure ("Failed to fetch")
    (typeof navigator !== 'undefined' && navigator.onLine === false)
  );
}

// Forgot-password INITIATION only. It triggers the Cognito reset email; the
// code-entry / new-password step lives on the /password-code bridge and the Halo
// mobile app, not here.
//
// Copy follows the app's ForgotPassword screen (app/src/screens/Auth/
// ForgotPassword.js): its heading, instruction and RESET PASSWORD button. The
// app's "Already have a reset code?" link is omitted because web has no code
// entry, and its header back-chevron becomes the BackLink at the top — the same
// affordance the signed-in web screens use — naming where it goes (Login).
export default function ForgotPassword({ onBackToSignIn }) {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const headingRef = useRef(null);

  // Move focus to the current heading when this view is shown (mounted from the
  // sign-in toggle) and when it switches to the confirmation, so screen readers
  // announce the change instead of silently swapping content.
  useEffect(() => {
    headingRef.current?.focus();
  }, [sent]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPending(true);
    setError('');
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      if (isTransportError(err)) {
        // Never left the machine — don't claim a link was sent.
        setError(
          "We couldn't reach Halo. Check your connection and try again.",
        );
      } else {
        // A Cognito-level response (including "no such account"): show the same
        // neutral confirmation either way, so it can't be used to enumerate emails.
        setSent(true);
      }
    } finally {
      setPending(false);
    }
  };

  const nav = (
    <div className="auth-screen__nav">
      <BackLink onClick={onBackToSignIn} label="Login" />
    </div>
  );

  if (sent) {
    return (
      <main className="app-shell auth-screen">
        {nav}
        <div className="auth-brand">
          <HaloLoginDevices />
        </div>
        <h1
          id="main-content"
          className="auth-screen__heading"
          ref={headingRef}
          tabIndex={-1}
        >
          Check your email
        </h1>
        <p className="auth-screen__intro">{CONFIRMATION}</p>
        <LegalFooter />
      </main>
    );
  }

  return (
    <main className="app-shell auth-screen">
      {nav}
      <div className="auth-brand">
        <HaloLoginDevices />
      </div>
      <h1
        id="main-content"
        className="auth-screen__heading"
        ref={headingRef}
        tabIndex={-1}
      >
        Forgot your password?
      </h1>
      <p className="auth-screen__intro">
        Please enter your verified email address to reset your password.
      </p>
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <TextField
          id="forgot-email"
          name="email"
          type="email"
          label="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={pending}
          required
          errorId={error ? 'forgot-error' : undefined}
        />
        {/* Always present so assistive tech has the live region before an error is
            set; only a transport failure ever populates it (see isTransportError). */}
        <p
          id="forgot-error"
          className="form-error"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </p>
        <button type="submit" className="auth-form__submit" disabled={pending}>
          {pending ? 'Sending…' : 'Reset Password'}
        </button>
      </form>
      <LegalFooter />
    </main>
  );
}
